import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { Button } from "@mui/material";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { roleToggler } from "../../redux-toolkit/features/access/accessSlice";
import {
  useGetBooksQuery,
  useResumeTaskQuery,
} from "../../redux-toolkit/features/books/bookApiSlice";
import ProgressBar from "../UI/ProgressBar";
import "./Home.css";
import Wheel from "./QuestWheel/Wheel";

import prevIconDark from "../../assets/dark buttons/next.svg";
import nextIconDark from "../../assets/dark buttons/previous.svg";
import nextIcon from "../../assets/next.svg";
import prevIcon from "../../assets/prev.svg";

import { useNavigate } from "react-router-dom";
import { useGetInitialAppliedThemeQuery } from "../../redux-toolkit/features/theme/themeApiSlice";
import { homeTabToggler } from "../../redux-toolkit/features/theme/themeSlice";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import lock from "../../assets/books/lock_book.gif";
import { TimeTrialPopup } from "../TimeTrails/TimeTrialPopup";

/**
 * Ongoing book = next after completed books (first unlocked incomplete by order).
 * Falls back to highest unlocked, then first book.
 */
export const resolveOngoingOrgBook = (books) => {
  if (!books?.length) return null;
  const sorted = [...books].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const isComplete = (b) => {
    const status = String(b?.status || "").toUpperCase();
    return status === "COMPLETE" || status === "COMPLETED";
  };

  const nextAfterCompleted = sorted.find(
    (b) => !b.isLocked && !isComplete(b),
  );
  if (nextAfterCompleted) return nextAfterCompleted;

  const unlocked = sorted.filter((b) => !b.isLocked);
  if (unlocked.length) return unlocked[unlocked.length - 1];

  return sorted[0];
};

/** Immediate previous org book before a locked book (by order). */
export const resolvePreviousOrgBook = (books, lockedBook) => {
  if (!books?.length || lockedBook?.order == null) {
    return resolveOngoingOrgBook(books);
  }
  const previous = [...books]
    .filter((b) => (b.order ?? 0) < lockedBook.order)
    .sort((a, b) => (b.order ?? 0) - (a.order ?? 0))[0];
  return previous || resolveOngoingOrgBook(books);
};

const BookIsLockedAlert = ({ open, setOpen, lastBook, alertType = "locked" }) => {
  const isUnpublished = alertType === "unpublished";

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
      }}
    >
      <DialogContent
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {isUnpublished ? (
          <InfoOutlinedIcon
            sx={{
              width: "70px",
              height: "70px",
              fontSize: "70px",
              color: "#3361cf",
            }}
          />
        ) : (
          <img
            src={lock}
            alt="lock"
            style={{
              width: "70px",
              height: "70px",
            }}
          />
        )}
        <DialogContentText id="alert-dialog-description">
          {isUnpublished ? (
            <>
              This book is yet to be <strong style={{ color: "#3361cf" }}>published</strong>.
            </>
          ) : (
            <>
              This book is <strong style={{ color: "#3361cf" }}>locked</strong>!
              Complete{" "}
              <strong style={{ color: "#3361cf" }}>{lastBook?.name}</strong> or wait
              for the admin to approve all your tasks.
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        style={{
          padding: "20px 24px",
        }}
      >
        <Button variant="contained" autoFocus onClick={() => setOpen(false)}>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const Home = ({
  isPreview,
  user,
  wheelColors,
  wheelBgColor,
  prevdividerImage,
  prevWheelBg,
  themeCardImage,
}) => {
  const [openLockAlert, setOpenLockAlert] = useState(false);
  const [lockAlertType, setLockAlertType] = useState("locked");
  const [activeOrgBook, setActiveOrgBook] = useState(null);
  const [activeCompanyBook, setActiveCompanyBook] = useState(null);
  const [lastBook, setLastBook] = useState(null);
  const [showTimeTrialPopup, setShowTimeTrialPopup] = useState(false);

  const {
    data: orgBooks,
    refetch,
  } = useGetBooksQuery("ORG", {
    refetchOnMountOrArgChange: true,
  });
  const homeTab = useSelector((state) => state.theme.homeTab);
  // homeTabToggler
  const primaryCompanyId = useSelector((state) => state.auth.user?.companyId);
  const {
    data: companyBooks,
  } = useGetBooksQuery(
    { type: "COMPANY", selectedCompany: primaryCompanyId },
    {
      refetchOnMountOrArgChange: true,
      skip: !primaryCompanyId,
    },
  );
  const { data } = useGetInitialAppliedThemeQuery({
    refetchOnMountOrArgChange: true,
  });
  const usedId = useSelector((state) => state.auth?.user?._id);
  const companyName = useSelector((state) => state.auth.user?.company);
  const {
    data: resumeTask,
  } = useResumeTaskQuery(usedId, {
    skip: !usedId,
    refetchOnMountOrArgChange: true,
  });
  const navigate = useNavigate();
  const orgWheelRef = useRef();
  const companyWheelRef = useRef();
  const orgBookInitializedRef = useRef(false);
  const companyBookInitializedRef = useRef(false);
  const dispatch = useDispatch();
  const [isExpanded] = useState(false);

  React.useEffect(() => {}, [data, homeTab]);
  const orgData = orgBooks?.data?.books ?? [];
  const companyData = React.useMemo(
    () =>
      (companyBooks?.data?.books ?? []).filter(
        (item) => item.isPublished === true,
      ),
    [companyBooks?.data?.books],
  );
  const ongoingOrgBook = React.useMemo(
    () => resolveOngoingOrgBook(orgData),
    [orgData],
  );
  const ongoingCompanyBook = React.useMemo(
    () => resolveOngoingOrgBook(companyData),
    [companyData],
  );
  const ongoingBook = homeTab ? ongoingOrgBook : ongoingCompanyBook;
  const resumeBookPayload = homeTab
    ? resumeTask?.data?.orgBook
    : resumeTask?.data?.companyBook;
  const resumeMatchesOngoing =
    !!resumeBookPayload?.book?._id &&
    !!ongoingBook?._id &&
    String(resumeBookPayload.book._id) === String(ongoingBook._id);
  const ongoingLevel = resumeMatchesOngoing ? resumeBookPayload?.level : null;

  React.useEffect(() => {
    refetch();
  }, [refetch]);

  React.useEffect(() => {
    if (!orgData.length) {
      setActiveOrgBook(null);
      orgBookInitializedRef.current = false;
      return;
    }
    if (!orgBookInitializedRef.current) {
      setActiveOrgBook(resolveOngoingOrgBook(orgData));
      orgBookInitializedRef.current = true;
    }
  }, [orgData]);

  React.useEffect(() => {
    companyBookInitializedRef.current = false;
    setActiveCompanyBook(null);
  }, [primaryCompanyId]);

  React.useEffect(() => {
    if (!companyData.length) {
      setActiveCompanyBook(null);
      companyBookInitializedRef.current = false;
      return;
    }
    if (!companyBookInitializedRef.current) {
      setActiveCompanyBook(resolveOngoingOrgBook(companyData));
      companyBookInitializedRef.current = true;
    }
  }, [companyData]);

  // Show Time Trial popup after 3 seconds
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowTimeTrialPopup(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // if (isLoading || loadingBooks) {
  //   return <ProgressBar />;
  // }

  React.useEffect(() => {
    dispatch(roleToggler("Employee"));
  }, []);

  const startHandler = (_id, isLocked) => {
    if (!isPreview) {
      const orgBooksList = orgBooks?.data?.books ?? [];
      const companyBooksList = companyBooks?.data?.books ?? [];
      const book = [...orgBooksList, ...companyBooksList].find(
        (b) => b._id === _id,
      );
      const isBlocked = isLocked || book?.isPublished === false;

      if (_id && !isBlocked) {
        navigate(`books/${_id}`);
      } else if (book?.isPublished === false) {
        setLockAlertType("unpublished");
        setOpenLockAlert(true);
      } else {
        const gateBook = resolvePreviousOrgBook(orgBooksList, book);
        setLastBook(gateBook);
        setLockAlertType("locked");
        setOpenLockAlert(true);
      }
    }
  };

  const resumeTaskHandler = (bookId, levelId) => {
    if (!bookId) return;
    if (levelId) {
      navigate(`/quest/books/${bookId}/levels/${levelId}`);
      return;
    }
    navigate(`/quest/books/${bookId}`);
  };
  const handleTabs = (tab) => {
    if (!isPreview) {
      dispatch(homeTabToggler(tab));
    }
  };

  const handleCloseTimeTrialPopup = () => {
    setShowTimeTrialPopup(false);
  };

  return (
    <div>
      <BookIsLockedAlert
        lastBook={lastBook}
        open={openLockAlert}
        setOpen={setOpenLockAlert}
        alertType={lockAlertType}
      />

      {data && (
        <div className="bg__books">
          <div
            id="layout"
            style={{ overflow: isPreview && "hidden" }}
            className="home__layout"
          >
            <div className="home__right">
              <div className="home__rightTop">
                <div
                  className="home__rightTop"
                  style={{
                    width: "100%",
                    opacity: isExpanded ? 0 : 1,
                    transform: isExpanded
                      ? "translateY(-150px)"
                      : "translateY(0px)",
                    transition: "0.4s all ease-in-out",
                  }}
                >
                  <h1 className="ftwt__r">
                    Unlock the power of engagement on The Quest!{" "}
                  </h1>
                  <h3 className="ftwt__r">
                    Through a variety of captivating activities, you'll deepen{" "}
                    <br /> your organizational understanding and create bonds
                    that foster growth.
                  </h3>
                  <img
                    style={{
                      width: "30%",
                    }}
                    alt="divider"
                    src={
                      isPreview && prevdividerImage
                        ? prevdividerImage
                        : data?.data?.dividerImage
                    }
                  />
                </div>
                <div className="step21">
                  <div
                    style={{
                      transform: isExpanded
                        ? "translateY(-150px)"
                        : "translateY(0px)",
                      transition: "0.4s all ease-in-out",
                    }}
                    className="home__btns home__rightswitch"
                  >
                    <h3
                      className={homeTab ? "home__activetab" : "home__tab"}
                      onClick={() => {
                        if (isPreview) return;
                        handleTabs(true);
                      }}
                    >
                      Organization
                    </h3>
                    {user?.role != "SUPERADMIN" && !user?.isCompanyAdmin && (
                      <h3
                        className={!homeTab ? "home__activetab" : "home__tab"}
                        onClick={() => {
                          if (isPreview) return;
                          handleTabs(false);
                        }}
                      >
                        {companyName || "Company"}
                      </h3>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {ongoingBook && (
              <div className="home__resume">
                <div>
                  <FiberManualRecordIcon
                    color="success"
                    sx={{ fontSize: "13px" }}
                  />
                  <h5 className="ftwt__r">Ongoing</h5>
                </div>
                <div className="home__resumeSecond">
                  <h6
                    onClick={() =>
                      resumeTaskHandler(ongoingBook._id, ongoingLevel?._id)
                    }
                    className="ftwt__r moving-text"
                  >
                    {ongoingBook.name}
                    {homeTab && ongoingLevel?.levelNumber != null ? (
                      <> &#62; &nbsp; Level {ongoingLevel.levelNumber}</>
                    ) : ongoingLevel?.name ? (
                      <> &#62; &nbsp; {ongoingLevel.name}</>
                    ) : null}
                  </h6>
                </div>
              </div>
            )}
            <div>
              <TimeTrialPopup
                isVisible={showTimeTrialPopup}
                onClose={handleCloseTimeTrialPopup}
              />
            </div>
            {(homeTab ? orgBooks : companyBooks) ? (
              <>
                {homeTab ? (
                  <div className="home__quest">
                    <Wheel
                      key={`ORG_WHEEL_${orgData.length}`}
                      wheelBgColor={wheelBgColor}
                      themeCardImage={themeCardImage}
                      activeTab={homeTab}
                      isExpanded={true}
                      ref={orgWheelRef}
                      storageKey="wheelScroll_ORG"
                      wheelColors={wheelColors}
                      isPreview={isPreview}
                      prevWheelBg={prevWheelBg}
                      startHandler={startHandler}
                      books={orgData}
                      focusBookId={ongoingOrgBook?._id}
                      getCenterItem={(item) => {
                        setActiveOrgBook(orgData[item] || null);
                      }}
                      activeBook={activeOrgBook}
                      setActiveBook={(book) => book && setActiveOrgBook(book)}
                    />
                  </div>
                ) : (
                  <div className="home__quest">
                    <Wheel
                      key={`COMPANY_WHEEL_${primaryCompanyId || "none"}_${companyData.length}`}
                      wheelBgColor={wheelBgColor}
                      themeCardImage={themeCardImage}
                      activeTab={homeTab}
                      isExpanded={true}
                      ref={companyWheelRef}
                      storageKey="wheelScroll_COMPANY"
                      wheelColors={wheelColors}
                      isPreview={isPreview}
                      prevWheelBg={prevWheelBg}
                      startHandler={startHandler}
                      books={companyData}
                      focusBookId={ongoingCompanyBook?._id}
                      getCenterItem={(item) => {
                        setActiveCompanyBook(companyData[item] || null);
                      }}
                      activeBook={activeCompanyBook}
                      setActiveBook={(book) =>
                        book && setActiveCompanyBook(book)
                      }
                    />
                  </div>
                )}
              </>
            ) : null}
            {(homeTab ? orgBooks : companyBooks) ? (
              <>
                <div className="quest__next">
                  <Button
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() =>
                      (homeTab ? orgWheelRef : companyWheelRef).current?.prev()
                    }
                  >
                    <img
                      src={data?.data.mode === "dark" ? nextIconDark : nextIcon}
                      alt="expand"
                    />
                  </Button>
                </div>
                <div className="quest__prev">
                  <Button
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() =>
                      (homeTab ? orgWheelRef : companyWheelRef).current?.next()
                    }
                  >
                    <img
                      src={data?.data.mode === "dark" ? prevIconDark : prevIcon}
                      alt="expand"
                    />
                  </Button>
                </div>
              </>
            ) : (
              <ProgressBar />
            )}

            {/* <div className="quest__expand">
                <Button
                  onClick={handleExpand}
                  focusRipple={false}
                  disableRipple
                  disableElevation
                >
                  <img
                    src={
                      data?.data.mode === "dark" ? expandIconDark : expandIcon
                    }
                    alt="expand"
                    style={{
                      width: "80%",
                      transform: isExpanded
                        ? "rotate(0deg)"
                        : "rotate(-180deg)",
                      transition: "0.4s  ease-in-out",
                    }}
                  />
                </Button>
              </div> */}
          </div>
        </div>
      )}
    </div>
  );
};
